<template>
  <div
    class="template-autocomplete"
    :class="{show}"
    v-scroll="calculateListPosition"
  >
    <vz-button
      big
      outline
      variant="primary"
      ref="target"
      icon-name="text-box-multiple"
      :title="label"
      @click="show = !show"
    >
      <span>{{ label }}</span>
      <vz-icon :name="menuIcon" :title="label" />
    </vz-button>

    <div
      v-show="show"
      ref="list"
      class="vz-new-autocomplete-list template-autocomplete-list vz-scroll"
      :class="`vz-new-autocomplete-list-${id}`"
    >
      <div class="template-autocomplete-list-items">
        <vz-loader :status-loader="useSpinner" without-img less-transparent />
        <vz-card
          v-if="showClearTemplate"
          class="template-autocomplete-list-item"
          :title="itemTitle(currentClearTemplate)"
          @click="onChange(currentClearTemplate)"
        >
          <span>{{ currentClearTemplate.name }}</span>
          <vz-tooltip-info text="Очистить все шаблоны и привести форму заказа к начальному виду" class="ml-5" />
        </vz-card>
        <vz-card
          v-for="item of items"
          class="template-autocomplete-list-item"
          @click="onChange(item)"
        >
          <vz-icon
            v-if="item.favorite"
            :variant="item.default ? 'primary' : 'warning'"
            :title="itemTitle(item)"
            class="ml-5"
            name="star"
            size="16"
          />
          <span>{{ item.name }}</span>
        </vz-card>

        <div v-if="isEmpty" class="vz-new-autocomplete-list-item active">
          <slot name="no-data">
            <div class="vz-new-autocomplete-list-item-title color-light">
              <span>Список избранных шаблонов пуст.</span>
              <nuxt-link :to="{ name: 'personal-order-template-id' }"> Новый шаблон</nuxt-link>
            </div>
          </slot>
        </div>
      </div>

      <a v-if="!useSpinner.value" class="template-autocomplete-more" @click="showMore">Показать все</a>
    </div>

    <vz-template-modal @select="onChange" />
  </div>
</template>

<script setup lang="ts">
import {TemplateOrder} from '~/types/order/orderV3';
import {getTemplateList} from '~/api/template';
import {onClickOutside} from '@vueuse/core';
import {storeToRefs} from 'pinia';
import {clearTemplate} from '~/common/objectsV3';
import {deepCopyObject} from '~/common/functions';
import {useCityStore} from '~/stores/city';
import {useOrderStore} from '~/stores/order';
import VzButton from "~/components/ui-kit/vz-button.vue";
import VzIcon from "~/components/ui-kit/vz-icon.vue";
import VzLoader from "~/components/ui-kit/vz-loader.vue";
import VzTooltipInfo from "~/components/ui-kit/vz-tooltip-info.vue";
import VzCard from "~/components/ui-kit/vz-card.vue";
import VzTemplateModal from "~/components/modals/vz-template-modal.vue";

const emit = defineEmits(['select'])

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  label: {
    type: String,
    default: 'Применить шаблон'
  }
})

const { $showDialog } = useNuxtApp()
const { rclCityList, currentCity } = storeToRefs(useCityStore())
const { appliedTemplates } = storeToRefs(useOrderStore())
const instance = getCurrentInstance()
const list = ref<HTMLElement>()
const target = ref<typeof VzButton>()
const id = ref(0)

const items = ref([] as TemplateOrder[])
const useSpinner = ref(false)
const show = ref(false)
let controller = new AbortController()

const menuIcon = computed(() => {
  return show.value ? 'menu-up' : 'menu-down'
})

const currentClearTemplate = computed(() => {
  const result: any = deepCopyObject(clearTemplate)
  result.content.directions.dispatch.locationGuid = currentCity.value.guid
  result.content.directions.destination.locationGuid = currentCity.value.guid === 'e90f19de-0128-11e5-80c7-00155d903d03'
    ? 'e90f1820-0128-11e5-80c7-00155d903d03'
    : 'e90f19de-0128-11e5-80c7-00155d903d03'

  return result
})

const showClearTemplate = computed(() => {
  return appliedTemplates.value.length
})

const isEmpty = computed(() => {
  return !items.value.length && !useSpinner.value
})

async function search(value?: string, favorite = 1) {
  controller.abort()
  useSpinner.value = true
  controller = new AbortController()
  items.value = (await getTemplateList({ name: value, favorite }, controller.signal)).data

  useSpinner.value = false
}

function calculateListPosition(): void {
  if (list.value && target.value) {
    const rect = list.value.getBoundingClientRect()
    const { top, right, height, width } = target.value.$el.getBoundingClientRect()

    const fullHeight = top + height + rect.height
    list.value.style.left = `${right - rect.width}px`
    list.value.style.width = `${width}px`

    if (fullHeight > window.innerHeight) {
      list.value.style.top = `${top - rect.height}px` // `-${rect.height - labelRect.height - 6}px`
      list.value.style.boxShadow = '2px -2px 4px rgba(0, 0, 0, 0.15)'
    } else {
      list.value.style.top = `${top + height}px`
      list.value.style.boxShadow = '0px 2px 4px rgba(0, 0, 0, 0.15)'
    }
  }
}

function itemTitle(template: TemplateOrder) {
  if (template.default) {
    return 'По умолчанию'
  }
  if (template.favorite) {
    return 'Избранное'
  }
  return ''
}

function onChange(value: TemplateOrder) {
  emit('select', value)
  show.value = false
}

function showMore() {
  show.value = false
  $showDialog({ name: 'vz-template-modal' })
}

onMounted(() => {
  search()
  id.value = instance?.uid || id.value
})

onClickOutside(target, (e: Event) => {
  if (show.value) {
    // Проверка для телефона
    if (e?.composedPath()?.find(target =>
      (target as Element)?.classList?.contains(`vz-new-autocomplete-list-${id.value}`))
    ) {
      return
    }
    show.value = false
  }
})

watch(() => show.value,
  async () => {
    if (show.value) {
      await nextTick()
      calculateListPosition()
    }
  }
)

onMounted(calculateListPosition)

defineExpose({
  search
})
</script>